// Sub-tenant pickers: registered sub-tenants as options, free text allowed
// (open-mode projects have sub-tenants that were never registered).

import { useMemo, type HTMLAttributes, type Key } from 'react'
import { Autocomplete, Box, TextField, Typography } from '@mui/material'

import { useListSubTenantsQuery } from '@/apis/v2Api'

function useSubTenantOptions(projectId: string) {
    const { data = [] } = useListSubTenantsQuery({ projectId }, { skip: !projectId })
    return useMemo(() => {
        const names = new Map(data.map(st => [st.external_id, st.name]))
        return { ids: data.map(st => st.external_id), names }
    }, [data])
}

function renderOption(names: Map<string, string>) {
    return function Option(props: HTMLAttributes<HTMLLIElement> & { key: Key }, id: string) {
        const { key, ...rest } = props
        const name = names.get(id)
        return (
            <Box component="li" key={key} {...rest}>
                <Box>
                    <Typography variant="body2">{name || id}</Typography>
                    {name && (
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                            {id}
                        </Typography>
                    )}
                </Box>
            </Box>
        )
    }
}

export function SubTenantMultiPicker({
    projectId,
    value,
    onChange,
    label = 'Clients (sub-tenants)',
    helperText = 'Pick registered sub-tenants or type an external id and press Enter.',
}: {
    projectId: string
    value: string[]
    onChange: (v: string[]) => void
    label?: string
    helperText?: string
}) {
    const { ids, names } = useSubTenantOptions(projectId)
    return (
        <Autocomplete
            multiple
            freeSolo
            autoSelect
            size="small"
            options={ids}
            value={value}
            onChange={(_, v) => onChange([...new Set(v.map(s => s.trim()).filter(Boolean))])}
            getOptionLabel={id => names.get(id) || id}
            renderOption={renderOption(names)}
            renderInput={params => <TextField {...params} label={label} helperText={helperText} />}
        />
    )
}

export function SubTenantSinglePicker({
    projectId,
    value,
    onChange,
    label = 'Sub-tenant',
    helperText,
}: {
    projectId: string
    value: string | null
    onChange: (v: string | null) => void
    label?: string
    helperText?: string
}) {
    const { ids, names } = useSubTenantOptions(projectId)
    return (
        <Autocomplete
            freeSolo
            autoSelect
            size="small"
            options={ids}
            value={value}
            onChange={(_, v) => onChange(v?.trim() ? v.trim() : null)}
            getOptionLabel={id => names.get(id) || id}
            renderOption={renderOption(names)}
            renderInput={params => <TextField {...params} label={label} helperText={helperText} />}
        />
    )
}
